import { useMemo } from 'react';
import type { WeightDay } from '@nutrigeist/shared';
import { formatNumber } from '../lib/format.js';

interface WeightChartProps {
  days: WeightDay[];
  /**
   * Which series to draw. Body fat uses the same geometry but its own values,
   * and only days that actually carry a reading — a weigh-in without a
   * body-fat measurement is a gap, not a zero.
   */
  metric?: 'weight' | 'bodyFat';
}

const METRICS = {
  weight: {
    label: 'Gewichtsverlauf',
    unit: 'kg',
    decimals: 1,
    average: (day: WeightDay): number | null => day.average,
    trend: (day: WeightDay): number | null => day.trend,
  },
  bodyFat: {
    label: 'Körperfettverlauf',
    unit: '%',
    decimals: 1,
    average: (day: WeightDay): number | null => day.bodyFatAverage,
    trend: (day: WeightDay): number | null => day.bodyFatTrend,
  },
} as const;

const WIDTH = 320;
const HEIGHT = 150;
const PADDING = { top: 8, right: 8, bottom: 18, left: 30 };

interface Point {
  x: number;
  y: number;
}

/**
 * A smooth curve through every point, as a cubic Bézier path.
 *
 * Control points come from Catmull-Rom: each point's tangent is the direction
 * between its neighbours, scaled by `TENSION`. Unlike a plain spline fit this
 * passes exactly through the data — a weight chart that merely floats *near*
 * the measurements would be a lie — while replacing the polyline's corners with
 * a continuous curve.
 */
const TENSION = 6;

function smoothPath(points: Point[]): string {
  if (points.length === 0) return '';

  const first = points[0]!;
  if (points.length === 1) return `M${first.x.toFixed(1)},${first.y.toFixed(1)}`;

  let path = `M${first.x.toFixed(1)},${first.y.toFixed(1)}`;

  for (let i = 0; i < points.length - 1; i += 1) {
    const p0 = points[i - 1] ?? points[i]!;
    const p1 = points[i]!;
    const p2 = points[i + 1]!;
    const p3 = points[i + 2] ?? p2;

    const c1 = { x: p1.x + (p2.x - p0.x) / TENSION, y: p1.y + (p2.y - p0.y) / TENSION };
    const c2 = { x: p2.x - (p3.x - p1.x) / TENSION, y: p2.y - (p3.y - p1.y) / TENSION };

    path +=
      ` C${c1.x.toFixed(1)},${c1.y.toFixed(1)}` +
      ` ${c2.x.toFixed(1)},${c2.y.toFixed(1)}` +
      ` ${p2.x.toFixed(1)},${p2.y.toFixed(1)}`;
  }

  return path;
}

/**
 * Weight over time: daily readings as points, the smoothed trend as a line.
 *
 * Hand-drawn SVG rather than a charting library — this is the only chart in the
 * app, and a dependency-free 100 lines beats shipping a general-purpose plotting
 * engine to a phone. The viewBox makes it fluid at any width.
 */
export function WeightChart({ days: allDays, metric = 'weight' }: WeightChartProps): JSX.Element {
  const spec = METRICS[metric];

  const geometry = useMemo(() => {
    // Only days carrying a reading for this metric take part; for body fat the
    // others are genuine gaps rather than zeroes.
    const days = allDays.filter((day) => spec.average(day) != null);
    if (days.length === 0) return null;

    const values = days.flatMap((day) => {
      const average = spec.average(day) as number;
      const trend = spec.trend(day);
      return trend == null ? [average] : [average, trend];
    });

    const min = Math.min(...values);
    const max = Math.max(...values);
    // Pad the domain so a flat series does not collapse onto a single line.
    const span = Math.max(max - min, 1);
    const domainMin = min - span * 0.15;
    const domainMax = max + span * 0.15;

    const plotWidth = WIDTH - PADDING.left - PADDING.right;
    const plotHeight = HEIGHT - PADDING.top - PADDING.bottom;

    const x = (index: number): number =>
      PADDING.left + (days.length === 1 ? plotWidth / 2 : (index / (days.length - 1)) * plotWidth);

    const y = (value: number): number =>
      PADDING.top + plotHeight - ((value - domainMin) / (domainMax - domainMin)) * plotHeight;

    const trendPath = smoothPath(
      days
        .map((day, index) => {
          const trend = spec.trend(day);
          return trend == null ? null : { x: x(index), y: y(trend) };
        })
        .filter((point): point is Point => point !== null),
    );

    // The daily averages the trend is smoothing. Drawn faintly underneath so
    // the distance between the two is legible: a smoothed line that lags a
    // sharp change looks broken on its own, but obviously correct next to the
    // raw line it is damping.
    const rawPath = smoothPath(
      days.map((day, index) => ({ x: x(index), y: y(spec.average(day) as number) })),
    );

    return {
      points: days.map((day, index) => ({
        cx: x(index),
        cy: y(spec.average(day) as number),
        date: day.date,
        value: spec.average(day) as number,
      })),
      count: days.length,
      trendPath,
      rawPath,
      domainMin,
      domainMax,
      yFor: y,
    };
  }, [allDays, spec]);

  if (!geometry) {
    return <div className="empty-state">Noch keine Messungen.</div>;
  }

  const { points, count, trendPath, rawPath, domainMin, domainMax, yFor } = geometry;
  const midValue = (domainMin + domainMax) / 2;

  return (
    <svg
      className="chart"
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      role="img"
      aria-label={spec.label}
      preserveAspectRatio="none"
      style={{ height: 170 }}
    >
      {[domainMax, midValue, domainMin].map((value) => (
        <g key={value}>
          <line
            x1={PADDING.left}
            x2={WIDTH - PADDING.right}
            y1={yFor(value)}
            y2={yFor(value)}
            stroke="var(--border)"
            strokeWidth="1"
          />
          <text
            className="chart-axis-label"
            x={PADDING.left - 4}
            y={yFor(value) + 3}
            textAnchor="end"
          >
            {formatNumber(value, spec.decimals)}
          </text>
        </g>
      ))}

      {rawPath && count > 1 && (
        <path
          className="chart-line"
          d={rawPath}
          stroke="var(--text-faint)"
          strokeWidth="1"
          opacity="0.5"
        />
      )}

      {trendPath && <path className="chart-line" d={trendPath} stroke="var(--accent)" />}

      {points.map((point) => (
        <circle
          key={point.date}
          cx={point.cx}
          cy={point.cy}
          r="2.5"
          fill="var(--text-muted)"
          opacity="0.75"
        >
          <title>{`${point.date}: ${formatNumber(point.value, spec.decimals)} ${spec.unit}`}</title>
        </circle>
      ))}
    </svg>
  );
}

/**
 * Names the two lines. Without it the smoothed line reads as "the data" and any
 * gap to the measured points looks like a bug rather than the smoothing.
 */
export function WeightChartLegend(): JSX.Element {
  return (
    <div className="row" style={{ gap: 14, flexWrap: 'wrap' }}>
      <span className="hint" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span
          style={{
            width: 14,
            height: 2,
            background: 'var(--accent)',
            borderRadius: 1,
            display: 'inline-block',
          }}
        />
        Trend (geglättet)
      </span>
      <span className="hint" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span
          style={{
            width: 14,
            height: 2,
            background: 'var(--text-faint)',
            opacity: 0.6,
            borderRadius: 1,
            display: 'inline-block',
          }}
        />
        Tagesmittel
      </span>
    </div>
  );
}
